import { GTM_EVENT_ALLOWLIST } from './events';
import type { CustomParams, EventName, EventParams } from './gtag.types';

/**
 * See Docs {@link https://developers.google.com/tag-platform/gtagjs/reference/events}
 * for particular GTM payload shapes and the
 * GA4 docs {@link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm}
 * for additional analytics context
 */
export const gtmEvent = (
  eventName: EventName | keyof typeof GTM_EVENT_ALLOWLIST,
  eventData: EventParams | CustomParams,
) => {
  window.gtag('event', eventName as EventName, {
    ...eventData,
    event_id: createEventUUID(),
  });
};

export const setGtmUserData = (
  email: string,
  firstName?: string,
  lastName?: string,
) => {
  Promise.all([
    hashSha256(email),
    firstName ? hashSha256(firstName) : Promise.resolve(),
    lastName ? hashSha256(lastName) : Promise.resolve(),
  ]).then(val => {
    window.gtag('set', 'user_data', {
      sha256_email_address: val[0],
      address: {
        sha256_first_name: val[1],
        sha256_last_name: val[2],
      },
    });
  });
};

/** Docs: https://developers.google.com/analytics/devguides/collection/ga4/reference/config#user_id */
export const setGtmUserID = (userId: string | null) =>
  window.gtag('set', 'user_id', userId as string);

/** Docs: https://developers.google.com/analytics/devguides/collection/ga4/reference/config#user_properties */
export const setGtmUserProperties = (userProperties: Record<string, unknown>) =>
  window.gtag('set', 'user_properties', userProperties);

/** e.g. when logging out the user */
export const resetAllGtmUserProps = () => {
  setGtmUserID(null);
  setGtmUserProperties({
    userTags: null,
    complianceType: null,
    organizationName: null,
    signupDate: null,
    onboardingDate: null,
  });
  window.gtag('set', 'user_data', {
    sha256_email_address: null,
    address: null,
  });
};

/** Unique per-event UUID for event de-duplication */
const createEventUUID = () => crypto.randomUUID();

/** Creates GTM compatible SHA256 hash */
const hashSha256 = async (message: string) => {
  // Encode the message as a Uint8Array
  const textEncoder = new TextEncoder();
  const data = textEncoder.encode(message);

  // Generate the SHA-256 hash
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Convert the ArrayBuffer to a hexadecimal string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hexHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hexHash;
};

export const TagNameLookup = {
  ud: 'user_designations',
  us: 'user_segments',
  udgr: 'user_designation_demographic_regions',
  ul: 'user_locations',
  lpt: 'license_plan_type',
  lps: 'license_plan_segment',
  lp$: 'license_purchase_type',
  lv: 'license_valid',
  o: 'organizations',
  p: 'plan_type',
  lvr: 'license_validation_reason',
} as const;

export const SingleValueTags = [
  TagNameLookup.lpt,
  TagNameLookup.lps,
  TagNameLookup.lp$,
  TagNameLookup.lv,
  TagNameLookup.lvr,
  TagNameLookup.p,
] as const;
